import React, { useRef, useState, useCallback, useEffect, memo } from 'react';
import SignaturePad from 'react-signature-canvas';
import styles from './SignatureCanvas.module.scss';
import Button from './Button';
import { Form } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { useInspection } from '../hooks/offline-hooks/createInspectionhook';
import { keys } from '../utilities/translator/translation_keys.js';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import Text from './typography/Text.jsx';
import { useCurrentUser } from '../providers/UserProvider';

export const SignatureField = memo(
  ({
    onSubmit,
    disabled,
    signatureText,
    setSignatureText,
    onSigned,
    showNameInput = true,
  }) => {
    const signatureRef = useRef(null);
    const { t } = useTranslation();
    const [signatureData, setSignatureData] = useState(null); // Store the drawn signature data
    const resizeCanvas = useCallback(() => {
      if (!signatureRef.current) return;
      const canvas = signatureRef.current.getCanvas();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      // clear() resets the library's internal _data array to match the now-blank
      // canvas; without this, _strokeUpdate can fire against stale internal state
      // and throw on undefined .points (signature_pad recommended resize pattern)
      signatureRef.current.clear();
      if (signatureData) {
        signatureRef.current.fromData(signatureData);
      }
    }, [signatureData]);

    useEffect(() => {
      window.addEventListener('resize', resizeCanvas);
      resizeCanvas(); // Adjust canvas size initially
      return () => {
        window.removeEventListener('resize', resizeCanvas);
      };
    }, [resizeCanvas]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => resizeCanvas(), []);

    const clearSignature = () => {
      signatureRef?.current?.clear();
      setSignatureData(null); // Reset the stored signature data
      setSignatureText('');
      onSigned(true);
    };

    const saveSignature = () => {
      const canSave = !!showNameInput ? signatureText?.length : true;
      if (!signatureRef.current.isEmpty() && canSave) {
        signatureRef.current.getCanvas().toBlob(
          (blob) => {
            const fileUUID = uuidv4();
            const file = new File([blob], `${fileUUID}.png`);
            onSubmit(signatureText, file);
            clearSignature();
          },
          'image/png',
          0.4,
        );
      }
    };

    const handleSignature = () => {
      const data = signatureRef.current.toData();
      setSignatureData(data);
      onSigned(false);
    };

    return (
      <div className={styles.signatureField}>
        <div className={styles.signatureCanvas}>
          <SignaturePad
            ref={signatureRef}
            penColor="black"
            canvasProps={{
              className: styles.signaturePadCanvas,
            }}
            onEnd={handleSignature}
            disabled={disabled}
          />
        </div>
        <div className={styles.buttonDisplay}>
          <Button
            value={t(keys.action.CLEAR)}
            size="sm"
            variant="secondary"
            onClick={clearSignature}
          />
          <Button
            value={t(keys.action.SUBMIT)}
            disabled={
              disabled ||
              signatureRef.current?.isEmpty() ||
              (!!showNameInput && !signatureText?.length)
            }
            size="sm"
            onClick={saveSignature}
          />
        </div>
      </div>
    );
  },
);

export default function SignatureCanvas({
  onSubmit,
  disabled = false,
  originalAdditional,
  showNameInput = true,
  required = false,
  preview = false,
}) {
  const { addImageResults } = useInspection();
  const { user: currentUser } = useCurrentUser();
  const [isSigned, setIsSigned] = useState(false);
  const [signatureText, setSignatureText] = useState('');

  const handleSubmitSignature = useCallback(
    (text, file) => {
      addImageResults('', onSubmit, file, {
        signatureText: text,
        originalAdditional,
      });
      setIsSigned(false);
    },
    [addImageResults, onSubmit, originalAdditional],
  );

  const handleUseSaved = useCallback(() => {
    const name = `${currentUser.firstName} ${currentUser.lastName}`;
    const additional = originalAdditional?.length
      ? `${originalAdditional}|${name}`
      : name;
    onSubmit(currentUser.signature, false, 'PASS', additional);
  }, [currentUser, onSubmit, originalAdditional]);

  return (
    <div className={styles.container}>
      {!!currentUser?.signature && !preview && (
        <div>
          <Text noMargin weight="semiBold">
            {t(keys.common.SAVED_SIGNATURE)}
          </Text>
          <div className={styles.savedSignature}>
            <img
              src={currentUser.signature}
              alt={t(keys.common.SAVED_SIGNATURE)}
              className={styles.savedSignaturePreview}
            />
            <Button
              icon="draw"
              size="sm"
              value={t(keys.common.USE_SAVED_SIGNATURE)}
              onClick={handleUseSaved}
              disabled={disabled}
            />
          </div>
          <div className={styles.divider}>
            <Text noMargin color="secondary" size="sm">
              {t(keys.common.OR_DRAW_NEW)}
            </Text>
          </div>
        </div>
      )}
      {!!showNameInput && (
        <>
          <Text noMargin weight="semiBold">
            {t(keys.assessments.FULL_NAME)}
          </Text>
          <Form.Control
            required
            placeholder={t(keys.action.ENTER, {
              variable: t(keys.common.NAME),
            })}
            type="text"
            value={signatureText}
            onChange={(e) => setSignatureText(e.target.value)}
            className={styles.nameInput}
          />
        </>
      )}
      <Text noMargin weight="semiBold">
        {t(keys.common.SIGNATURE)}
        {!!required ? <span className={styles.red}>*</span> : null}
      </Text>
      <SignatureField
        showNameInput={showNameInput}
        onSubmit={handleSubmitSignature}
        disabled={disabled || isSigned || preview}
        signatureText={signatureText}
        setSignatureText={setSignatureText}
        onSigned={setIsSigned}
      />
    </div>
  );
}
